import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./pluginSidebar.html";
import { CLAUDEFORCE_LISTING_ID } from '~/constants';
import { aiAssetEngagement } from '~/modules/analytics/gtmEventTypes/aiAssetAnalyticsContract';
import { AGENTEXCHANGE_SITE_URL } from '~/modules/config';
import { Routes } from '~/modules/routes';
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { getExternalInstallConfig } from '~/modules/utilities/external_install_utils';
import { getClaudePluginInstallCommands, getPluginSource } from '~/modules/utilities/plugin_utils';
import { getSkillSupportedHarnesses, isPrivateSkillSource } from '~/modules/utilities/skill_utils';
const CLAUDE_CODE_INSTALL_METHOD_ID = 'claude-code';
const CLAUDEFORCE_ACCESS_INTERNAL_DRIVER = 'listing-sidebar';

// The install options a plugin's Claude harness can offer, in primary-CTA precedence.

const INSTALL_OPTION_TABS = {
  prompt: 'AI Prompt',
  cli: 'CLI Script'
};
/**
 * Plugin install sidebar — the plugin-listing counterpart to `listing/skillSidebar`, embedded by
 * `pages/listingDetailV2` in the sticky left panel's top slot. Same install box (primary CTA, "More
 * options" disclosure, View on GitHub, guided-install modals) driven by the plugin extension's
 * `supportedHarnesses`, but Claude-only: plugin installation uses Claude's marketplace command, so
 * there is no method dropdown. Other native plugins stay title-only until authored install data
 * exists. The source coordinates remain independent and feed only the "View on GitHub" CTA.
 */
class PluginSidebar extends LightningElement {
  constructor(...args) {
    super(...args);
    this.listing = void 0;
    this.requestBetaAccess = false;
    this.labels = void 0;
    // The secondary install options sit behind a "More options" disclosure, collapsed by default.
    // Initialized (not merely declared) so re-assigning it re-renders under the client build.
    this.moreOptionsExpanded = false;
  }
  renderedCallback() {
    const isExternalInstall = this.isExternalInstall;
    const showNativeInstallUi = this.showNativeInstallUi;
    if (this.announcedListing === this.listing && this.announcedExternalInstall === isExternalInstall && this.announcedNativeInstallUi === showNativeInstallUi) {
      return;
    }
    this.announcedListing = this.listing;
    this.announcedExternalInstall = isExternalInstall;
    this.announcedNativeInstallUi = showNativeInstallUi;
    if (showNativeInstallUi) {
      this.dispatchInstallStateChange();
    }
  }
  get isClaudeforceAccessListing() {
    return this.listing?.external_id === CLAUDEFORCE_LISTING_ID;
  }
  get claudeforceAccessUrl() {
    const url = new URL(`${AGENTEXCHANGE_SITE_URL.replace(/\/$/, '')}/${Routes.CLAUDEFORCE_BETA_ACCESS}`);
    url.searchParams.set('d', CLAUDEFORCE_ACCESS_INTERNAL_DRIVER);
    return url.toString();
  }
  get requestBetaAccessHeading() {
    return this.labels?.requestBetaAccessHeading ?? '';
  }
  get getInstallPromptLabel() {
    return this.labels?.getInstallPrompt ?? '';
  }
  get cliScriptLabel() {
    return this.labels?.commandLineTab ?? '';
  }
  get viewOnGitHubLabel() {
    return this.labels?.viewOnGitHub ?? '';
  }
  get headingLabel() {
    if (this.requestBetaAccess) {
      return this.labels?.requestBetaAccessHeading ?? '';
    }
    if (this.isExternalInstall && this.isClaudeforceAccessListing) {
      return this.labels?.externalActionHeading ?? '';
    }
    // `{HARNESS}` needs the raw `i18nStore` proxy; the wired `labels` proxy always calls message
    // functions with no arguments.
    if (this.showNativeInstallUi) {
      const formatter = i18nStore.getState().i18n?.skills?.installForHarnessHeading;
      return formatter?.({
        HARNESS: this.labels?.installMethodClaude ?? ''
      }) ?? '';
    }
    return this.labels?.installPluginHeading ?? '';
  }

  /** Harness whose icon leads the "Install for Claude" heading; '' without native CTAs. */
  get singleHarnessId() {
    return this.showNativeInstallUi ? CLAUDE_CODE_INSTALL_METHOD_ID : '';
  }
  get externalInstallConfig() {
    return getExternalInstallConfig(this.listing);
  }
  get isExternalInstall() {
    return this.externalInstallConfig.isExternal;
  }
  get externalInstallUrl() {
    if (this.requestBetaAccess && this.isClaudeforceAccessListing) {
      return this.claudeforceAccessUrl;
    }
    return this.isClaudeforceAccessListing ? this.externalInstallConfig.installUrl : '';
  }
  get externalInstallTarget() {
    return this.requestBetaAccess && this.isClaudeforceAccessListing ? '_self' : '_blank';
  }
  get hasExternalInstallUrl() {
    return !!this.externalInstallUrl;
  }
  get showRequestBetaAccessCopy() {
    return this.requestBetaAccess && this.hasExternalInstallUrl;
  }
  get requestBetaAccessDescription() {
    return this.labels?.requestBetaAccessDescription ?? '';
  }
  get externalInstallLabel() {
    return this.requestBetaAccess ? this.labels?.requestBetaAccessCta ?? '' : this.labels?.externalActionCta ?? '';
  }
  get externalInstallAriaLabel() {
    const listingName = this.listing?.name?.trim();
    const externalActionFormatter = this.requestBetaAccess ? i18nStore.getState().i18n?.skills?.requestBetaAccessAriaLabel : i18nStore.getState().i18n?.skills?.externalActionAriaLabel;
    if (listingName && externalActionFormatter) {
      return externalActionFormatter({
        LISTING_NAME: listingName
      });
    }
    const formatter = i18nStore.getState().i18n?.listingCard?.opensInNewTabLabel;
    return formatter?.({
      TITLE: this.externalInstallLabel
    }) || this.externalInstallLabel;
  }

  // The plugin's GitHub source URL, for the "View on GitHub" CTA. '' when the listing carries no
  // parseable source or the repo is private, in which case the link is hidden (see hasSourceUrl).
  get sourceUrl() {
    const source = getPluginSource(this.listing);
    return source && !isPrivateSkillSource(source) ? source.url : '';
  }
  get hasSourceUrl() {
    return !!this.sourceUrl;
  }

  /** Native CTAs require both a published Claude harness and the authored SF Skills marketplace
   *  association that supplies the install command. */
  get showNativeInstallUi() {
    return !this.requestBetaAccess && !this.isExternalInstall && this.installOptions.length > 0;
  }

  // The real Claude marketplace install command. Unknown plugin listings fail closed until the
  // listing schema supplies authored marketplace metadata (see plugin_utils).
  get installCommand() {
    return getClaudePluginInstallCommands(this.listing)?.cli ?? '';
  }

  // Plugins install through Claude's marketplace only, so other published harnesses appear in the
  // metadata card's "Works with" row but get no CTA here.
  get claudeHarness() {
    return getSkillSupportedHarnesses(this.listing).find(harness => harness.installMethodId === CLAUDE_CODE_INSTALL_METHOD_ID);
  }

  // The Claude harness's published install options, in primary-CTA precedence.
  get installOptions() {
    const harness = this.claudeHarness;
    if (!harness || !this.installCommand) {
      return [];
    }
    const options = [];
    if (harness.hasPrompt) {
      options.push('prompt');
    }
    if (harness.hasCli) {
      options.push('cli');
    }
    return options;
  }
  get primaryOption() {
    return this.installOptions[0];
  }
  get isPrimaryPrompt() {
    return this.primaryOption === 'prompt';
  }
  get isPrimaryCli() {
    return this.primaryOption === 'cli';
  }
  get hasSecondaryCli() {
    return this.installOptions.slice(1).includes('cli');
  }
  get moreOptionsLabel() {
    return (this.moreOptionsExpanded ? this.labels?.lessOptions : this.labels?.moreOptions) ?? '';
  }

  /** Hides (rather than unmounts) the More options panel so the toggle's `aria-controls` always
   *  resolves to a present element. */
  get moreOptionsCollapsed() {
    return !this.moreOptionsExpanded;
  }
  get moreOptionsIcon() {
    return this.moreOptionsExpanded ? 'chevronup' : 'chevrondown';
  }
  get moreOptionsAriaExpanded() {
    return this.moreOptionsExpanded ? 'true' : 'false';
  }

  // The install state listingDetailV2 attributes page-level engagement to: the primary option.
  get installationTab() {
    return INSTALL_OPTION_TABS[this.primaryOption ?? 'prompt'];
  }
  focusPrimaryInstallControl(options) {
    this.template?.querySelector('.external-install-action, .primary-install-action')?.focus(options);
  }
  handleToggleMoreOptions() {
    this.moreOptionsExpanded = !this.moreOptionsExpanded;
  }

  // Opens the shared guided-install modal, where plugin prompts resolve to Claude's native
  // `/plugin install` command. Pass the trigger so wds-modal can return focus when it closes.
  handleGetInstallPrompt(event) {
    this.showModal('[data-id="install-prompt-modal"]', event);
  }
  handleCliScript(event) {
    this.showModal('[data-id="cli-script-modal"]', event);
  }
  showModal(selector, event) {
    const modal = this.template?.querySelector(selector);
    modal?.show?.(event.currentTarget);
  }

  // Stamp the install method onto the CTA's auto-emitted analytics event so the click carries the
  // target agent (always 'claude-code' for plugins).
  handleGetInstallPromptAnalytics(event) {
    event.detail.installMethod = CLAUDE_CODE_INSTALL_METHOD_ID;
    event.detail.aiAssetAnalytics = this.installOptionEngagement('Get Install Prompt', 'AI Prompt');
  }
  handleCliScriptAnalytics(event) {
    event.detail.installMethod = CLAUDE_CODE_INSTALL_METHOD_ID;
    event.detail.aiAssetAnalytics = this.installOptionEngagement('CLI Script', 'CLI Script');
  }
  handleViewSourceAnalytics(event) {
    event.detail.aiAssetAnalytics = this.installOptionEngagement('View Source', this.installationTab, this.sourceUrl);
  }
  handleMoreOptionsAnalytics(event) {
    // wds-button emits the analytics event before the click flips `moreOptionsExpanded`, so the
    // current state describes the action the user is taking next.
    event.detail.aiAssetAnalytics = aiAssetEngagement({
      aiAssetKind: 'plugin',
      clickText: this.moreOptionsExpanded ? 'Less Options' : 'More Options',
      elementTitle: 'Install This Plugin',
      elementType: 'link text',
      installationTab: this.installationTab,
      installationMethodId: CLAUDE_CODE_INSTALL_METHOD_ID
    });
  }
  handleExternalInstallAnalytics(event) {
    event.detail.aiAssetAnalytics = aiAssetEngagement({
      aiAssetKind: 'plugin',
      clickText: this.externalInstallLabel,
      clickUrl: this.externalInstallUrl,
      elementTitle: this.headingLabel,
      elementType: 'button',
      installationTab: 'External'
    });
  }
  installOptionEngagement(clickText, installationTab, clickUrl) {
    return aiAssetEngagement({
      aiAssetKind: 'plugin',
      clickText,
      ...(clickUrl ? {
        clickUrl
      } : {}),
      elementTitle: 'Install This Plugin',
      elementType: 'button',
      installationTab,
      installationMethodId: CLAUDE_CODE_INSTALL_METHOD_ID
    });
  }
  dispatchInstallStateChange() {
    this.dispatchEvent(new CustomEvent('marketplaceinstallstatechange', {
      detail: {
        installationTab: this.installationTab,
        installationMethodId: CLAUDE_CODE_INSTALL_METHOD_ID
      }
    }));
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(PluginSidebar, {
  publicProps: {
    listing: {
      config: 0
    },
    requestBetaAccess: {
      config: 0
    }
  },
  publicMethods: ["focusPrimaryInstallControl"],
  wire: {
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: i18n => i18n.skills
        };
      }
    }
  },
  fields: ["moreOptionsExpanded"]
});
const __lwc_component_class_internal = _registerComponent(PluginSidebar, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;